import React, { useEffect, useRef, useState } from 'react';
import { GameState } from '../../../shared/types';
import { qrCodeGenerator } from '../utils/qrcode';

interface GameDisplayProps {
  gameState: GameState | null;
  gameId: string;
  pitchData?: {
    player1: { pitch: number; timestamp: number } | null;
    player2: { pitch: number; timestamp: number } | null;
  };
  connected?: boolean;
  reconnecting?: boolean;
  error?: string | null;
  gamePaused?: boolean;
  pausedWaitingFor?: 1 | 2 | null;
}

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 400;
const PADDLE_WIDTH = 10;
const PADDLE_HEIGHT = 80;
const BALL_SIZE = 10;

export function GameDisplay({ 
  gameState, 
  gameId, 
  pitchData, 
  connected = true, 
  reconnecting = false, 
  error = null, 
  gamePaused = false, 
  pausedWaitingFor = null 
}: GameDisplayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [qrCodeError, setQrCodeError] = useState<string>('');
  const [joinURL, setJoinURL] = useState<string>('');

  // Generate QR code when gameId changes
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        setQrCodeError('');
        // In development, use the React dev server (port 3000) for the controller URL
        // In production, use the current origin
        const clientURL = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:3000' // React dev server for controller interface
          : window.location.origin;
        
        // Generate controller URL directly instead of using the /join redirect
        const generatedJoinURL = `${clientURL}/controller?gameId=${gameId}`;
        const qrCode = await qrCodeGenerator.generateQRCode(generatedJoinURL);
        setQrCodeDataURL(qrCode);
        setJoinURL(generatedJoinURL);
      } catch (error) {
        console.error('Failed to generate QR code:', error);
        setQrCodeError('Failed to generate QR code');
      }
    };

    if (gameId) {
      generateQRCode();
    }
  }, [gameId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up canvas
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;

    // Render game
    renderGame(ctx, gameState);
  }, [gameState]);

  const renderGame = (ctx: CanvasRenderingContext2D, state: GameState | null) => {
    // Clear canvas
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    if (!state) {
      // Show waiting state
      ctx.fillStyle = '#fff';
      ctx.font = '24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Waiting for game...', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
      return;
    }

    // Draw center line
    ctx.strokeStyle = '#fff';
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(CANVAS_WIDTH / 2, 0);
    ctx.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw paddles
    ctx.fillStyle = '#fff';
    
    // Player 1 paddle (left) - coordinates are already in pixels
    ctx.fillRect(20, state.paddles.player1.y, PADDLE_WIDTH, PADDLE_HEIGHT);
    
    // Player 2 paddle (right) - coordinates are already in pixels
    ctx.fillRect(CANVAS_WIDTH - 30, state.paddles.player2.y, PADDLE_WIDTH, PADDLE_HEIGHT);

    // Draw ball - coordinates are already in pixels
    ctx.fillRect(state.ball.x - BALL_SIZE / 2, state.ball.y - BALL_SIZE / 2, BALL_SIZE, BALL_SIZE);

    // Draw scores
    ctx.font = '48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(state.score.player1.toString(), CANVAS_WIDTH / 4, 60);
    ctx.fillText(state.score.player2.toString(), (3 * CANVAS_WIDTH) / 4, 60);

    // Draw game status
    if (state.gameStatus === 'countdown' && state.countdown > 0) {
      // Draw countdown with enhanced styling
      ctx.font = '96px Arial';
      ctx.fillStyle = '#ffd700';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 4;
      const countdownText = Math.ceil(state.countdown).toString();
      ctx.strokeText(countdownText, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
      ctx.fillText(countdownText, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
      
      // Add "Get Ready!" text
      ctx.font = '32px Arial';
      ctx.fillStyle = '#fff';
      ctx.fillText('Get Ready!', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 80);
      
      // Add calibration instruction
      ctx.font = '20px Arial';
      ctx.fillStyle = '#ccc';
      ctx.fillText('Players can move paddles to calibrate voice', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 60);
    } else if (state.gameStatus === 'waiting') {
      ctx.font = '24px Arial';
      ctx.fillStyle = '#fff';
      ctx.fillText('Waiting for players...', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 50);
    } else if (state.gameStatus === 'playing') {
      // Show "GAME ON!" briefly when game starts
      ctx.font = '24px Arial';
      ctx.fillStyle = '#0f0';
      ctx.fillText('GAME ON!', CANVAS_WIDTH / 2, 100);
    } else if (state.gameStatus === 'paused') {
      // Game paused - show pause message
      ctx.font = '48px Arial';
      ctx.fillStyle = '#ff6b35';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.strokeText('PAUSED', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 40);
      ctx.fillText('PAUSED', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 40);
      
      // Show waiting message
      ctx.font = '24px Arial';
      ctx.fillStyle = '#fff';
      const waitingText = state.pausedWaitingFor 
        ? `Waiting for Player ${state.pausedWaitingFor} to rejoin...`
        : 'Waiting for player to rejoin...';
      ctx.fillText(waitingText, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 20);
    } else if (state.gameStatus === 'ended') {
      // Game ended - show winner and final score
      ctx.font = '48px Arial';
      ctx.fillStyle = '#ffd700';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      const winner = state.score.player1 > state.score.player2 ? 'Player 1' : 'Player 2';
      const winnerText = `${winner} Wins!`;
      ctx.strokeText(winnerText, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 40);
      ctx.fillText(winnerText, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 40);
      
      // Show final score
      ctx.font = '32px Arial';
      ctx.fillStyle = '#fff';
      const finalScoreText = `Final Score: ${state.score.player1} - ${state.score.player2}`;
      ctx.fillText(finalScoreText, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 20);
      
      // Show instruction for new game
      ctx.font = '20px Arial';
      ctx.fillStyle = '#ccc';
      ctx.fillText('Players can start a new game from their controllers', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 60);
    }

    // Player connection status is now shown in the QR code section instead of on canvas
  };

  // Determine if we should show QR code based on game state
  const shouldShowQRCode = !gameState || 
    gameState.gameStatus === 'waiting' || 
    gameState.gameStatus === 'paused' ||
    (!gameState.players.player1 || !gameState.players.player2);

  return (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Game ID: {gameId}</h2>
        
        {/* Connection Status Indicator */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          padding: '8px 12px',
          borderRadius: '20px',
          backgroundColor: connected ? '#d4edda' : reconnecting ? '#fff3cd' : '#f8d7da',
          border: `1px solid ${connected ? '#c3e6cb' : reconnecting ? '#ffeaa7' : '#f5c6cb'}`,
          fontSize: '14px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: connected ? '#28a745' : reconnecting ? '#ffc107' : '#dc3545'
          }} />
          <span style={{ 
            color: connected ? '#155724' : reconnecting ? '#856404' : '#721c24',
            fontWeight: '500'
          }}>
            {connected ? 'Connected' : reconnecting ? 'Reconnecting...' : 'Disconnected'}
          </span>
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          color: '#721c24',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          Connection Error: {error}
        </div>
      )}
      
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '40px', flexWrap: 'wrap' }}>
        {/* Game Canvas */}
        <div>
          <canvas
            ref={canvasRef}
            style={{
              border: '2px solid #fff',
              backgroundColor: '#000',
              display: 'block'
            }}
          />
        </div>
        
        {/* QR Code Section */}
        {shouldShowQRCode && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            backgroundColor: '#f0f0f0',
            padding: '20px',
            borderRadius: '10px',
            minWidth: '280px'
          }}>
            <h3 style={{ color: '#333', marginTop: 0 }}>Join the Game</h3>
            <p style={{ color: '#666', margin: '0 0 15px 0', fontSize: '14px' }}>
              Scan with your phone to join as a controller
            </p>
            
            {qrCodeError ? (
              <div style={{ color: '#f00', padding: '20px' }}>
                {qrCodeError}
              </div>
            ) : qrCodeDataURL ? (
              <a 
                href={joinURL} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  display: 'block',
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
                title="Click to open controller in new tab"
              >
                <img 
                  src={qrCodeDataURL} 
                  alt="QR Code to join game"
                  style={{ 
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </a>
            ) : (
              <div style={{ 
                width: '256px', 
                height: '256px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#eee',
                border: '2px solid #ddd',
                borderRadius: '8px',
                color: '#666'
              }}>
                Generating QR Code...
              </div>
            )}
            
            {/* Connection Status */}
            <div style={{ marginTop: '15px', fontSize: '14px' }}>
              <div style={{ 
                color: gameState?.players.player1 ? '#0a0' : '#666',
                marginBottom: '5px'
              }}>
                Player 1: {gameState?.players.player1 ? '✓ Connected' : 'Waiting...'}
              </div>
              <div style={{ 
                color: gameState?.players.player2 ? '#0a0' : '#666'
              }}>
                Player 2: {gameState?.players.player2 ? '✓ Connected' : 'Waiting...'}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Pitch Data Display - Show when players are connected */}
      {gameState && (gameState.players.player1 || gameState.players.player2) && (
        <div style={{ 
          marginTop: '30px',
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          {/* Player 1 Pitch Data */}
          {gameState.players.player1 && (
            <div style={{
              backgroundColor: '#f8f9fa',
              border: '2px solid #e9ecef',
              borderRadius: '10px',
              padding: '20px',
              minWidth: '200px',
              textAlign: 'center'
            }}>
              <h4 style={{ color: '#495057', margin: '0 0 15px 0' }}>Player 1 Voice</h4>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: pitchData?.player1 ? '#28a745' : '#6c757d',
                marginBottom: '10px'
              }}>
                {pitchData?.player1 
                  ? `${Math.round(pitchData.player1.pitch)} Hz`
                  : 'No pitch detected'
                }
              </div>
              <div style={{
                fontSize: '12px',
                color: '#6c757d'
              }}>
                {pitchData?.player1 && (
                  `Last update: ${new Date(pitchData.player1.timestamp).toLocaleTimeString()}`
                )}
              </div>
              <div style={{
                marginTop: '10px',
                height: '4px',
                backgroundColor: '#e9ecef',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  backgroundColor: pitchData?.player1 ? '#28a745' : '#e9ecef',
                  width: pitchData?.player1 ? '100%' : '0%',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}
          
          {/* Player 2 Pitch Data */}
          {gameState.players.player2 && (
            <div style={{
              backgroundColor: '#f8f9fa',
              border: '2px solid #e9ecef',
              borderRadius: '10px',
              padding: '20px',
              minWidth: '200px',
              textAlign: 'center'
            }}>
              <h4 style={{ color: '#495057', margin: '0 0 15px 0' }}>Player 2 Voice</h4>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: pitchData?.player2 ? '#17a2b8' : '#6c757d',
                marginBottom: '10px'
              }}>
                {pitchData?.player2 
                  ? `${Math.round(pitchData.player2.pitch)} Hz`
                  : 'No pitch detected'
                }
              </div>
              <div style={{
                fontSize: '12px',
                color: '#6c757d'
              }}>
                {pitchData?.player2 && (
                  `Last update: ${new Date(pitchData.player2.timestamp).toLocaleTimeString()}`
                )}
              </div>
              <div style={{
                marginTop: '10px',
                height: '4px',
                backgroundColor: '#e9ecef',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  backgroundColor: pitchData?.player2 ? '#17a2b8' : '#e9ecef',
                  width: pitchData?.player2 ? '100%' : '0%',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}